import {t} from 'i18next';

import {getAppDisplayName} from './appUtils';
import {CatalogApp, isReplitApp} from './catalogApp';
import {getCatalogAppAuthorLdap, normalizeCatalogValue} from './catalogFilters';

export const OTHER_ORG_ID = 'other';

export type CatalogOrgSection = {
  apps: CatalogApp[];
  id: string;
  isViewerOrg: boolean;
  /** Org display name, or null for the catch-all section of apps without a known org. */
  label: string | null;
  /** Show owner actions on each app (the viewer's own apps). */
  withActions?: boolean;
};

/** Workday org strings read like "Product Team"; the suffix is noise in a tab. */
export const getOrgLabel = (workdayTeam?: string | null): string | null =>
  workdayTeam?.trim().replace(/\s+team$/i, '') || null;

export const getOrgId = (label: string) => `org:${normalizeCatalogValue(label)}`;

export const getOrgSectionLabel = (section: CatalogOrgSection) =>
  section.label ?? t('apps_org_other');

/** DIG accent hues for org shelves, ordered so neighbors differ. Red reads as an error, so skip it. */
export const ORG_ACCENTS = [
  'blue',
  'green',
  'purple',
  'orange',
  'cyan',
  'magenta',
  'yellow',
] as const;
export type OrgAccent = (typeof ORG_ACCENTS)[number];

/**
 * Gives each named org a hue in shelf order, so neighboring shelves never share one. Sections
 * hidden by a search keep their slot, so colors don't shift while typing. The catch-all section
 * stays neutral.
 */
export const getOrgAccents = (sections: readonly CatalogOrgSection[]) =>
  new Map<string, OrgAccent>(
    sections
      .filter((section) => section.label !== null)
      .map((section, index) => [section.id, ORG_ACCENTS[index % ORG_ACCENTS.length]])
  );

const getPublishedTime = (app: CatalogApp) => {
  const publishedAt = isReplitApp(app) ? app.published_at : undefined;
  const time = publishedAt ? Date.parse(publishedAt) : NaN;
  return Number.isNaN(time) ? null : time;
};

/** Newest first; apps without a publish date follow, by name. */
export const compareCatalogApps = (left: CatalogApp, right: CatalogApp) => {
  const leftTime = getPublishedTime(left);
  const rightTime = getPublishedTime(right);
  if (leftTime !== null && rightTime !== null && leftTime !== rightTime) {
    return rightTime - leftTime;
  }
  if (leftTime !== null && rightTime === null) return -1;
  if (leftTime === null && rightTime !== null) return 1;
  return getAppDisplayName(left).localeCompare(getAppDisplayName(right), undefined, {
    sensitivity: 'base',
  });
};

/**
 * Groups apps by their author's org (`orgByLdap` is keyed by normalized LDAP).
 * The viewer's org comes first, then orgs by app count; apps without a known org go last.
 */
export const buildCatalogOrgSections = (
  apps: readonly CatalogApp[],
  orgByLdap: ReadonlyMap<string, string>,
  viewerOrg: string | null
): CatalogOrgSection[] => {
  const sections = new Map<string, CatalogOrgSection>();
  for (const app of apps) {
    const ldap = getCatalogAppAuthorLdap(app);
    const label = (ldap && orgByLdap.get(ldap)) || null;
    const id = label ? getOrgId(label) : OTHER_ORG_ID;
    const section = sections.get(id) ?? {
      apps: [],
      id,
      isViewerOrg: Boolean(label && viewerOrg && getOrgId(viewerOrg) === id),
      label,
    };
    section.apps.push(app);
    sections.set(id, section);
  }

  const rank = (section: CatalogOrgSection) =>
    section.isViewerOrg ? 0 : section.id === OTHER_ORG_ID ? 2 : 1;

  return [...sections.values()]
    .map((section) => ({...section, apps: [...section.apps].sort(compareCatalogApps)}))
    .sort(
      (left, right) =>
        rank(left) - rank(right) ||
        right.apps.length - left.apps.length ||
        (left.label ?? '').localeCompare(right.label ?? '', undefined, {sensitivity: 'base'})
    );
};
